import React, { useState } from 'react';
import {
  BookOpen,
  Database,
  FileText,
  ArrowRight,
  ArrowDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Layers,
  Calculator,
  GitBranch,
  Shield,
  RefreshCw,
  Globe,
  Link2,
  HelpCircle,
  BarChart3,
} from 'lucide-react';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'sources', label: 'Data Sources' },
  { id: 'flow', label: 'Data Flow' },
  { id: 'matching', label: 'Account Matching' },
  { id: 'calculation', label: 'Calculation Logic' },
  { id: 'gaps', label: 'Data Gaps' },
  { id: 'currency', label: 'Currency Conversion' },
  { id: 'summary', label: 'Summary Totals' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

// ─── tiny reusable pieces ──────────────────────────────────────────────
const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => {
  const map: Record<string, string> = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    zinc: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${map[color] || map.zinc}`}>
      {children}
    </span>
  );
};

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] ${className}`}>
    {children}
  </div>
);

const SectionHeading: React.FC<{ id: string; icon: React.ReactNode; title: string; subtitle?: string }> = ({ id, icon, title, subtitle }) => (
  <div id={id} className="scroll-mt-8 mb-5">
    <div className="flex items-center gap-3 mb-1">
      <span className="text-emerald-400">{icon}</span>
      <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-zinc-400 ml-9">{subtitle}</p>}
  </div>
);

const Collapsible: React.FC<{ title: string; badge?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, badge, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[hsl(225,15%,18%)] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium text-zinc-200 hover:bg-[hsl(225,15%,12%)] transition-colors"
      >
        {open ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
        <span className="flex-1">{title}</span>
        {badge}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-[hsl(225,15%,15%)]">{children}</div>}
    </div>
  );
};

// ─── Flow diagram pieces ───────────────────────────────────────────────
const FlowNode: React.FC<{ icon: React.ReactNode; label: string; sub?: string; color?: string }> = ({ icon, label, sub, color = 'emerald' }) => {
  const ring: Record<string, string> = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    blue: 'border-blue-500/30 bg-blue-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
    cyan: 'border-cyan-500/30 bg-cyan-500/5',
    red: 'border-red-500/30 bg-red-500/5',
    zinc: 'border-zinc-500/30 bg-zinc-500/5',
  };
  return (
    <div className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${ring[color] || ring.zinc}`}>
      <span className="shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-zinc-200 truncate">{label}</div>
        {sub && <div className="text-[10px] text-zinc-500 truncate">{sub}</div>}
      </div>
    </div>
  );
};

const FlowArrow: React.FC<{ label?: string; vertical?: boolean }> = ({ label, vertical }) => (
  <div className={`flex ${vertical ? 'flex-col items-center py-1' : 'items-center px-1'}`}>
    {vertical
      ? <ArrowDown className="h-4 w-4 text-zinc-600" />
      : <ArrowRight className="h-4 w-4 text-zinc-600" />}
    {label && <span className="text-[9px] text-zinc-500 whitespace-nowrap">{label}</span>}
  </div>
);

// ─── main component ────────────────────────────────────────────────────
const RaketechDocs: React.FC = () => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* ── header ─────────────────────────────────── */}
      <div className="mb-8 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-emerald-400" />
          Raketech Reconciliation Documentation
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">
          Data sources, matching logic, gap analysis, and calculation methodology — Reconciliation period (Mar 2023 – Nov 2024)
        </p>
      </div>

      <div className="flex gap-8 items-start">
        {/* ── sticky sidebar / TOC ─────────────────── */}
        <nav className="hidden xl:block w-52 shrink-0 sticky top-6">
          <Card className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-2 px-2">On this page</p>
            <ul className="space-y-0.5">
              {SECTIONS.map(s => (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    className="w-full text-left px-2 py-1.5 rounded-md text-xs text-zinc-400 hover:text-white hover:bg-[hsl(225,15%,14%)] transition-colors"
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        {/* ── content column ───────────────────────── */}
        <div className="flex-1 min-w-0 max-w-4xl space-y-10">

          {/* ═══════ OVERVIEW ═══════ */}
          <section>
            <SectionHeading id="overview" icon={<BookOpen className="h-5 w-5" />} title="Overview" subtitle="Why this system exists and what it covers" />
            <Card className="p-5 space-y-3">
              <p className="text-sm text-zinc-300 leading-relaxed">
                Raketech acts as an intermediary affiliate network. They manage relationships with operators on our behalf, and we receive commission through them.
                Because no single data source gives us a complete and trustworthy picture, we triangulate from <strong className="text-white">six independent data sources</strong> to determine how much we actually earned vs. how much we were paid.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge color="amber">Period: Mar 2023 – Nov 2024</Badge>
                <Badge color="emerald">6 data sources</Badge>
                <Badge color="blue">Multi-currency (10+)</Badge>
                <Badge color="purple">EUR-normalized comparison</Badge>
              </div>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 mt-2">
                <p className="text-xs text-amber-300 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>This document covers only the reconciliation period. The post-recon period (Dec 2024+), which relies on Affiliation Cloud and DataSync, is documented separately.</span>
                </p>
              </div>
            </Card>
          </section>

          {/* ═══════ DATA SOURCES ═══════ */}
          <section>
            <SectionHeading id="sources" icon={<Database className="h-5 w-5" />} title="Data Sources" subtitle="Six sources, each providing a different lens on the same earnings" />
            <div className="space-y-3">

              <Collapsible title="1. Actual Invoices" badge={<Badge color="emerald">Ground truth</Badge>} defaultOpen>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    The actual invoices we received from Raketech. This is the definitive record of what they paid us — lump-sum totals per invoice, not broken down by account.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="Faktnr" value="Invoice number" />
                    <DataField label="Faktdatum" value="Invoice date" />
                    <DataField label="Totalt" value="Total amount (EUR)" />
                  </div>
                  <TableRef table="raketech_actual_invoices" sheet="Invoices" />
                  <Limitation text="Lump-sum only — no per-account breakdown. Tells us total paid, not how it was distributed." />
                </div>
              </Collapsible>

              <Collapsible title="2. Commission Reports (Monthly)" badge={<Badge color="amber">Per month</Badge>}>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Raketech's monthly reports of what they calculated as our earnings, broken down by brand/partner/geo.
                    One sheet per month (e.g. "January 2024 Commission"). Always in EUR.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="Brand" value="Operator (may have ID)" />
                    <DataField label="Partner" value="Partner / network" />
                    <DataField label="Offer" value="Offer ID ⚠️" />
                    <DataField label="GEO" value="Geographic market" />
                    <DataField label="FTDs / QFTDs" value="Player counts" />
                    <DataField label="Total Revenue" value="CPA + RS (EUR)" />
                  </div>
                  <TableRef table="raketech_invoices" sheet="{Month} {Year} Commission" />
                  <Limitation text="Offer ID column is missing in some months — forces fuzzy brand+geo matching instead of precise offer-based matching." />
                </div>
              </Collapsible>

              <Collapsible title="3. Reconciliation Report" badge={<Badge color="blue">Raketech's audit</Badge>}>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Raketech's own corrected reconciliation covering the full period. Per-account, per-month, with recalculated payout rates. This is their double-check — their most granular data.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="ID" value="Raketech account ID" />
                    <DataField label="Username" value="Account username" />
                    <DataField label="Currency" value="Account currency" />
                    <DataField label="Month" value="Earning month" />
                    <DataField label="Total new earnings" value="Corrected total" />
                    <DataField label="Reports difference" value="Original vs corrected" />
                  </div>
                  <TableRef table="raketech_reconciliation" sheet="Reconciliation" />
                  <div className="text-xs text-zinc-500 flex items-start gap-1.5 mt-1">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    Most granular source. Multi-currency. Correction rows tracked separately (is_correction = 1).
                  </div>
                </div>
              </Collapsible>

              <Collapsible title="4. Account Reporting (Manual Backend Checks)" badge={<Badge color="cyan">Our verification</Badge>}>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Our manual verification — we logged into operator backends directly and checked what the operator reported for each account.
                    Compares Raketech's numbers against actual operator data.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="Our Total Commission" value="What we found in backend" />
                    <DataField label="RT - Total new earnings" value="What Raketech reported" />
                    <DataField label="Tot commission diff" value="The difference" />
                    <DataField label="Backend" value="Affiliate platform" />
                    <DataField label="Comment" value="Check status / notes" />
                    <DataField label="Currency" value="Account currency" />
                  </div>
                  <TableRef table="raketech_partner_accounts" sheet="Account Reporting" />
                  <Limitation text="Not all accounts accessible — shared accounts (Raketech owns it, we're sub-affiliate) and some platforms have no backend access." />
                  <Limitation text="Aggregate snapshot (full period), not monthly breakdown." />
                </div>
              </Collapsible>

              <Collapsible title="5. Verified Accounts (Recon Accounts Not Verified)" badge={<Badge color="purple">Additional checks</Badge>}>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Accounts in the reconciliation that we verified through other means — typically accounts we couldn't access via normal backend login. Provides additional manual-check data points.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="ID (D)" value="Raketech account ID" />
                    <DataField label="ID in Rec. Report" value="Reconciliation ID" />
                    <DataField label="Amount" value="Verified commission" />
                  </div>
                  <TableRef table="raketech_verified_accounts" sheet="Recon Accounts Not Verified" />
                </div>
              </Collapsible>

              <Collapsible title="6. DataSync (adwstats synced data)" badge={<Badge color="emerald">Automated</Badge>}>
                <div className="space-y-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Automated data pulled from operator backends through adwstats' sync system. For accounts where we have API access, monthly statistics are synced including commission and FTDs.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <DataField label="commissions_amount" value="Synced commission" />
                    <DataField label="stat_month" value="Month of statistics" />
                    <DataField label="currency" value="Synced currency" />
                  </div>
                  <TableRef table="statistics_monthly" sheet="" />
                  <Limitation text="Only accounts set up in adwstats with working credentials. Many Raketech sub-accounts aren't directly accessible." />
                  <Limitation text="Requires account matching (Raketech ID → adwstats UUID) via mapping or AC ID bridge." />
                </div>
              </Collapsible>

            </div>
          </section>

          {/* ═══════ DATA FLOW ═══════ */}
          <section>
            <SectionHeading id="flow" icon={<GitBranch className="h-5 w-5" />} title="Data Flow" subtitle="From Google Sheets through processing to the comparison output" />
            <Card className="p-5">
              {/* Row 1: Import sources */}
              <div className="mb-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Step 1 — Import from Google Sheets</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <FlowNode icon={<Globe className="h-3.5 w-3.5 text-amber-400" />} label="Exchange Rates" sub="FX tab" color="amber" />
                  <FlowNode icon={<FileText className="h-3.5 w-3.5 text-amber-400" />} label="Commission Reports" sub="Monthly sheets" color="amber" />
                  <FlowNode icon={<Shield className="h-3.5 w-3.5 text-blue-400" />} label="Reconciliation" sub="Full period audit" color="blue" />
                  <FlowNode icon={<FileText className="h-3.5 w-3.5 text-emerald-400" />} label="Actual Invoices" sub="Faktnr / Totalt" color="emerald" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  <FlowNode icon={<Database className="h-3.5 w-3.5 text-cyan-400" />} label="Account Reporting" sub="Manual backend checks" color="cyan" />
                  <FlowNode icon={<Database className="h-3.5 w-3.5 text-purple-400" />} label="Verified Accounts" sub="Additional checks" color="purple" />
                  <FlowNode icon={<Link2 className="h-3.5 w-3.5 text-zinc-400" />} label="RT Account Check" sub="ID mapping sheet" color="zinc" />
                </div>
              </div>

              <div className="flex justify-center py-2">
                <div className="flex flex-col items-center">
                  <ArrowDown className="h-5 w-5 text-emerald-500" />
                  <span className="text-[10px] text-emerald-400 font-medium">POST /api/raketech/import</span>
                  <span className="text-[9px] text-zinc-500">Parse, clean, store in D1</span>
                </div>
              </div>

              {/* Row 2: D1 storage */}
              <div className="mb-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Step 2 — Stored in Cloudflare D1</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {[
                    'raketech_reconciliation',
                    'raketech_invoices',
                    'raketech_partner_accounts',
                    'raketech_verified_accounts',
                    'raketech_actual_invoices',
                    'raketech_exchange_rates',
                    'raketech_account_mapping',
                    'raketech_invoice_brand_mapping',
                  ].map(t => (
                    <div key={t} className="bg-[hsl(225,15%,13%)] rounded px-2 py-1.5 text-[10px] text-zinc-400 font-mono truncate border border-[hsl(225,15%,18%)]">
                      {t}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center py-2">
                <div className="flex flex-col items-center">
                  <ArrowDown className="h-5 w-5 text-emerald-500" />
                  <span className="text-[10px] text-emerald-400 font-medium">GET /api/raketech/compare</span>
                  <span className="text-[9px] text-zinc-500">Load, match, convert, compare</span>
                </div>
              </div>

              {/* Row 3: Processing */}
              <div className="mb-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Step 3 — Processing Pipeline</p>
                <div className="flex flex-col md:flex-row items-stretch gap-2">
                  <div className="flex-1">
                    <FlowNode icon={<RefreshCw className="h-3.5 w-3.5 text-amber-400" />} label="Historical FX Rates" sub="ECB + fawazahmed0 + fallbacks" color="amber" />
                  </div>
                  <div className="flex items-center justify-center"><ArrowRight className="h-4 w-4 text-zinc-600 hidden md:block" /><ArrowDown className="h-4 w-4 text-zinc-600 md:hidden" /></div>
                  <div className="flex-1">
                    <FlowNode icon={<Link2 className="h-3.5 w-3.5 text-blue-400" />} label="Account Matching" sub="Mapping → AC ID → name" color="blue" />
                  </div>
                  <div className="flex items-center justify-center"><ArrowRight className="h-4 w-4 text-zinc-600 hidden md:block" /><ArrowDown className="h-4 w-4 text-zinc-600 md:hidden" /></div>
                  <div className="flex-1">
                    <FlowNode icon={<Layers className="h-3.5 w-3.5 text-purple-400" />} label="Invoice Matching" sub="Brand+Offer+GEO cascade" color="purple" />
                  </div>
                  <div className="flex items-center justify-center"><ArrowRight className="h-4 w-4 text-zinc-600 hidden md:block" /><ArrowDown className="h-4 w-4 text-zinc-600 md:hidden" /></div>
                  <div className="flex-1">
                    <FlowNode icon={<Calculator className="h-3.5 w-3.5 text-emerald-400" />} label="Per-Account Compare" sub="MAX(recon, sync×.95, manual×.95)" color="emerald" />
                  </div>
                </div>
              </div>

              <div className="flex justify-center py-2">
                <div className="flex flex-col items-center">
                  <ArrowDown className="h-5 w-5 text-emerald-500" />
                  <span className="text-[10px] text-emerald-400 font-medium">Output</span>
                </div>
              </div>

              {/* Row 4: Output */}
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Step 4 — Report Pages</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <FlowNode icon={<BarChart3 className="h-3.5 w-3.5 text-emerald-400" />} label="Per Account" sub="/raketech/accounts" color="emerald" />
                  <FlowNode icon={<AlertTriangle className="h-3.5 w-3.5 text-red-400" />} label="Commission Errors" sub="/raketech/underpaid" color="red" />
                  <FlowNode icon={<FileText className="h-3.5 w-3.5 text-red-400" />} label="Report" sub="/raketech/report" color="red" />
                  <FlowNode icon={<RefreshCw className="h-3.5 w-3.5 text-cyan-400" />} label="Unsynced" sub="/raketech/unsynced" color="cyan" />
                </div>
              </div>
            </Card>
          </section>

          {/* ═══════ ACCOUNT MATCHING ═══════ */}
          <section>
            <SectionHeading id="matching" icon={<Link2 className="h-5 w-5" />} title="Account Matching" subtitle="How accounts are linked across Raketech's system and ours" />

            <div className="grid md:grid-cols-2 gap-4">
              {/* Account matching */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-400" />
                  Account ID Matching Cascade
                </h3>
                <ol className="space-y-3">
                  <MatchStep step={1} label="Manual Mapping" desc="User-created overrides in raketech_account_mapping" color="emerald" />
                  <MatchStep step={2} label="Auto RT Account Check" desc="Auto-generated from the RT Account Check sheet (Raketech ID → Rec Report ID → AC ID → adwstats UUID)" color="blue" />
                  <MatchStep step={3} label="AC ID Bridge" desc='Raketech account_id matches Affiliation Cloud ID (accounts named "AC-{id}" in adwstats)' color="purple" />
                </ol>
              </Card>

              {/* Invoice matching */}
              <Card className="p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-amber-400" />
                  Invoice Matching Cascade
                </h3>
                <ol className="space-y-3">
                  <MatchStep step={1} label="Brand + Offer + GEO" desc="Normalized brand name + offer ID + geo (most precise)" color="emerald" />
                  <MatchStep step={2} label="Partner + Offer + GEO" desc="Fallback when brand names differ between systems" color="blue" />
                  <MatchStep step={3} label="Brand + GEO (no offer)" desc="Used when offer ID is missing — less precise, potentially ambiguous" color="amber" />
                  <MatchStep step={4} label="Manual Invoice Mapping" desc="User-created link: invoice brand key → reconciliation account" color="purple" />
                  <MatchStep step={5} label="Offer ID Cross-Reference" desc="Match invoice offer against any recon/partner account with the same offer" color="cyan" />
                </ol>
              </Card>
            </div>

            <Card className="p-4 mt-4">
              <p className="text-xs text-zinc-400 leading-relaxed">
                <strong className="text-zinc-200">Brand name normalization:</strong> Both brand and partner names are normalized before comparison — lowercased, suffixes like .com/.se removed, common words like "casino"/"betting"/"sports" stripped, whitespace and punctuation removed. This allows "LeoVegas Casino.com" to match "leovegas" in the reconciliation data.
              </p>
            </Card>
          </section>

          {/* ═══════ CALCULATION LOGIC ═══════ */}
          <section>
            <SectionHeading id="calculation" icon={<Calculator className="h-5 w-5" />} title="Calculation Logic" subtitle='How "Should Be" (total owed) is derived from available data' />

            <Card className="p-5 space-y-5">
              {/* The formula */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Per-Account Income Formula</h3>
                <div className="bg-[hsl(225,15%,7%)] rounded-lg border border-[hsl(225,15%,15%)] p-4 font-mono text-xs space-y-2">
                  <div className="text-zinc-400">
                    <span className="text-emerald-400">income</span> = <span className="text-amber-400">MAX</span>(
                  </div>
                  <div className="pl-6 space-y-1">
                    <div><span className="text-blue-400">reconciliation_earnings_eur</span><span className="text-zinc-600">,</span> <span className="text-zinc-500">// Raketech's corrected recon — no deduction</span></div>
                    <div><span className="text-emerald-400">synced_from_operator</span> <span className="text-zinc-500">×</span> <span className="text-amber-300">0.95</span><span className="text-zinc-600">,</span> <span className="text-zinc-500">// Our synced data minus ~5% Raketech share</span></div>
                    <div><span className="text-cyan-400">manual_check_amount</span> <span className="text-zinc-500">×</span> <span className="text-amber-300">0.95</span> <span className="text-zinc-500">// Manual backend check minus ~5%</span></div>
                  </div>
                  <div className="text-zinc-400">)</div>
                  <div className="border-t border-[hsl(225,15%,18%)] pt-2 mt-2">
                    <span className="text-red-400">underpaid</span> = <span className="text-zinc-300">invoice_total</span> − <span className="text-emerald-400">income</span>
                    <span className="text-zinc-500 ml-3">// negative = underpaid</span>
                  </div>
                </div>
              </div>

              {/* Source breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Income Sources & Adjustments</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[hsl(225,15%,18%)]">
                        <th className="text-left py-2 px-3 text-zinc-500 font-medium">Source</th>
                        <th className="text-left py-2 px-3 text-zinc-500 font-medium">Value</th>
                        <th className="text-left py-2 px-3 text-zinc-500 font-medium">Adjustment</th>
                        <th className="text-left py-2 px-3 text-zinc-500 font-medium">Why</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                      <tr>
                        <td className="py-2.5 px-3"><Badge color="blue">Reconciliation</Badge></td>
                        <td className="py-2.5 px-3 text-zinc-300">total_new_earnings (per month → EUR)</td>
                        <td className="py-2.5 px-3 text-zinc-300">None</td>
                        <td className="py-2.5 px-3 text-zinc-400">Already net of Raketech's share</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3"><Badge color="emerald">DataSync</Badge></td>
                        <td className="py-2.5 px-3 text-zinc-300">commissions_amount (per month → EUR)</td>
                        <td className="py-2.5 px-3 text-amber-300 font-mono">× 0.95</td>
                        <td className="py-2.5 px-3 text-zinc-400">Operator reports gross; Raketech keeps ~5%</td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3"><Badge color="cyan">Manual Check</Badge></td>
                        <td className="py-2.5 px-3 text-zinc-300">our_total_commission or verified_amount</td>
                        <td className="py-2.5 px-3 text-amber-300 font-mono">× 0.95</td>
                        <td className="py-2.5 px-3 text-zinc-400">Same as DataSync — gross from backend</td>
                      </tr>
                      <tr className="bg-[hsl(225,15%,8%)]">
                        <td className="py-2.5 px-3"><Badge color="amber">Invoice</Badge></td>
                        <td className="py-2.5 px-3 text-zinc-300">total_revenue from commission reports</td>
                        <td className="py-2.5 px-3 text-zinc-500">N/A</td>
                        <td className="py-2.5 px-3 text-zinc-400">The other side — what Raketech charged</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Why MAX? */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-emerald-300 mb-2 flex items-center gap-1.5">
                    <HelpCircle className="h-3.5 w-3.5" /> Why MAX?
                  </h4>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Each source has gaps. By taking the maximum we ensure that if recon underreports, synced or manual data catches it. If we lack synced data, recon alone determines income. This gives the <strong className="text-zinc-200">most defensible estimate</strong>.
                  </p>
                </div>
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-amber-300 mb-2 flex items-center gap-1.5">
                    <HelpCircle className="h-3.5 w-3.5" /> Why the 5% deduction?
                  </h4>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Operator backends report <strong className="text-zinc-200">gross commission</strong>. Raketech acts as intermediary and takes ~5% as their fee. The reconciliation report already reflects this deduction, so only synced and manual data need the adjustment.
                  </p>
                </div>
              </div>

              {/* Manual check priority */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Manual Check Priority</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  When both Verified Accounts and Account Reporting exist for the same account:
                </p>
                <ol className="mt-2 space-y-1.5 text-xs text-zinc-400">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 font-mono font-bold shrink-0">1.</span>
                    <span><strong className="text-zinc-200">Verified Accounts</strong> (raketech_verified_accounts) checked first</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400 font-mono font-bold shrink-0">2.</span>
                    <span><strong className="text-zinc-200">Account Reporting</strong> (our_total_commission) used if its EUR value is higher</span>
                  </li>
                </ol>
                <p className="text-xs text-zinc-500 mt-1.5">The highest value wins — best available evidence.</p>
              </div>
            </Card>
          </section>

          {/* ═══════ DATA GAPS ═══════ */}
          <section>
            <SectionHeading id="gaps" icon={<AlertTriangle className="h-5 w-5" />} title="Data Gaps & Limitations" subtitle="Known issues and how we mitigate them" />
            <div className="space-y-3">

              <GapCard
                number={1}
                title="Missing Offer IDs in Commission Reports"
                severity="high"
                problem="Several monthly commission sheets lack an Offer column entirely. Without offer IDs, invoice rows can only be matched by brand+geo — ambiguous when multiple accounts share the same brand and geo."
                impact="Invoice revenue may be attributed to the wrong account or left unmatched."
                mitigations={[
                  'Brand ID resolution: if a month has a brand_id, the system looks up what offer that brand_id maps to in months that do have the Offer column',
                  'Manual invoice brand mapping: users can explicitly link unmatched invoices to recon accounts',
                  'No-offer rows are supplemented into the first matching brand+geo account',
                ]}
              />

              <GapCard
                number={2}
                title="Limited Backend Access (Shared / Raketech-Owned)"
                severity="high"
                problem="Many accounts are shared (Raketech owns the operator account, we're a sub-affiliate) or entirely Raketech-owned. Some platforms don't expose APIs or manual logins."
                impact="No synced data and no manual check for these accounts. We rely solely on Raketech's reconciliation — trusting their numbers."
                mitigations={[
                  'Account Reporting tracks which accounts we could and couldn\'t verify (check_status)',
                  'Accounts without independent verification fall back to reconciliation data alone',
                ]}
              />

              <GapCard
                number={3}
                title="Currency Conversion Complexity"
                severity="medium"
                problem="Accounts span 10+ currencies (EUR, USD, GBP, COP, CLP, PEN, ARS, BRL…). Commission reports are EUR but reconciliation data is in native currency."
                impact="Exchange rate differences between earning time and conversion time create small discrepancies. Exotic currency rates may be unavailable."
                mitigations={[
                  'Multi-layered rate resolution: ECB → fawazahmed0 → hardcoded COP → Sheet rates → period average → fallback API',
                  'Historical per-month conversion rather than single rate',
                  'KV-cached rates for performance',
                ]}
              />

              <GapCard
                number={4}
                title='Unmatched Invoice Entries ("No Accounts Tracked")'
                severity="critical"
                problem="Some commission report rows have no corresponding recon account, partner account, or synced data."
                impact='Revenue Raketech says we earned that we cannot verify or identify. Shown as "no_accounts_tracked" critical issues.'
                mitigations={[
                  'Manual invoice brand mapping for explicit linking',
                  'Offer ID cross-referencing catches some automatically',
                ]}
              />

              <GapCard
                number={5}
                title="Reconciliation Corrections"
                severity="low"
                problem='Correction rows (ID = "Corrections") adjust previous months up or down but lack proper account IDs.'
                impact="Tracked in summary totals but excluded from per-account comparison. May slightly skew the overall picture."
                mitigations={[
                  'Positive and negative corrections are summed separately and shown in summary',
                ]}
              />

              <GapCard
                number={6}
                title="Account ID Mismatches Between Sources"
                severity="medium"
                problem="Raketech's account IDs aren't consistent across their own data sources. The recon ID may differ from Account Reporting which may differ from the AC ID."
                impact="Accounts appear unmatched even though they represent the same underlying affiliate account."
                mitigations={[
                  'RT Account Check sheet provides the ID crosswalk (auto-generates mappings on import)',
                  'Manual mapping overrides for edge cases',
                ]}
              />

            </div>
          </section>

          {/* ═══════ CURRENCY ═══════ */}
          <section>
            <SectionHeading id="currency" icon={<Globe className="h-5 w-5" />} title="Currency Conversion" subtitle="Multi-layered FX rate resolution for EUR normalization" />
            <Card className="p-5">
              <div className="space-y-2">
                {[
                  { step: 1, label: 'Monthly Historical Rate', source: 'Frankfurter/ECB API', note: 'Primary — EUR-focused, major currencies', color: 'emerald' },
                  { step: 2, label: 'Supplementary Historical', source: 'fawazahmed0/currency-api', note: 'COP, CLP, PEN, ARS, BRL…', color: 'blue' },
                  { step: 3, label: 'Hardcoded COP Rates', source: 'Yahoo Finance', note: 'Monthly COP/EUR for 2023-03 to 2024-03', color: 'amber' },
                  { step: 4, label: 'Sheet-Provided Rate', source: 'Google Sheets Exchange Rates tab', note: 'Fallback from reconciliation spreadsheet', color: 'purple' },
                  { step: 5, label: 'Period Average', source: 'Computed', note: 'Average of all available monthly rates for that currency', color: 'cyan' },
                  { step: 6, label: 'Current Rate (last resort)', source: 'ExchangeRate-API', note: 'Only if nothing else available', color: 'red' },
                ].map(r => (
                  <div key={r.step} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(225,15%,12%)] transition-colors">
                    <span className={`flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold border ${
                      r.color === 'emerald' ? 'border-emerald-500/40 text-emerald-400 bg-emerald-500/10' :
                      r.color === 'blue' ? 'border-blue-500/40 text-blue-400 bg-blue-500/10' :
                      r.color === 'amber' ? 'border-amber-500/40 text-amber-400 bg-amber-500/10' :
                      r.color === 'purple' ? 'border-purple-500/40 text-purple-400 bg-purple-500/10' :
                      r.color === 'cyan' ? 'border-cyan-500/40 text-cyan-400 bg-cyan-500/10' :
                      'border-red-500/40 text-red-400 bg-red-500/10'
                    }`}>{r.step}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-zinc-200">{r.label}</span>
                      <span className="text-xs text-zinc-500 ml-2">— {r.source}</span>
                    </div>
                    <span className="text-[10px] text-zinc-500 hidden sm:block">{r.note}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-3 pt-3 border-t border-[hsl(225,15%,15%)]">
                All rates are KV-cached — 90-day TTL for API rates, 365-day TTL for historical supplementary rates.
              </p>
            </Card>
          </section>

          {/* ═══════ SUMMARY TOTALS ═══════ */}
          <section>
            <SectionHeading id="summary" icon={<BarChart3 className="h-5 w-5" />} title="Summary Totals" subtitle="What each aggregate number means and how it's calculated" />
            <Card className="p-0 overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[hsl(225,15%,8%)] border-b border-[hsl(225,15%,18%)]">
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Metric</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Calculation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                  <SummaryRow label="Invoiced" desc="SUM of total_revenue from all commission report line items (EUR)" />
                  <SummaryRow label="Actual Invoiced" desc="SUM of total_amount from actual invoices tab — what Raketech literally paid us" />
                  <SummaryRow label="Should Be" desc="SUM of per-account MAX(recon, synced×0.95, manual×0.95) — our best estimate of true earnings" highlight />
                  <SummaryRow label="Recon Earnings" desc="SUM of reconciliation total_new_earnings, converted to EUR per-month with historical rates" />
                  <SummaryRow label="Partner Our Share" desc="SUM of Account Reporting our_total_commission × 0.95, converted to EUR" />
                  <SummaryRow label="Adwstats Synced" desc="SUM of DataSync commissions, converted to EUR per-month with historical rates" />
                  <SummaryRow label="Manual Check" desc="SUM of verified / manual-check amounts, converted to EUR" />
                  <SummaryRow label="Positive Corrections" desc="SUM of correction rows with positive total_new_earnings (increases owed)" />
                  <SummaryRow label="Negative Corrections" desc="SUM of correction rows with negative total_new_earnings (decreases owed)" />
                </tbody>
              </table>
            </Card>

            <Card className="p-4 mt-4 border-emerald-500/20">
              <p className="text-xs text-zinc-300 leading-relaxed">
                <strong className="text-emerald-400">The key comparison:</strong> <span className="font-mono text-amber-300">Actual Invoiced</span> vs <span className="font-mono text-emerald-300">Should Be</span> — the difference represents the total discrepancy between what we were paid and what we can prove we earned.
              </p>
            </Card>
          </section>

        </div>
      </div>
    </div>
  );
};

// ─── sub-components ──────────────────────────────────────────────────
const DataField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-[hsl(225,15%,8%)] rounded-md px-2.5 py-1.5 border border-[hsl(225,15%,15%)]">
    <div className="text-[10px] text-zinc-500 font-medium">{label}</div>
    <div className="text-[11px] text-zinc-300 truncate">{value}</div>
  </div>
);

const TableRef: React.FC<{ table: string; sheet: string }> = ({ table, sheet }) => (
  <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-1">
    <span className="flex items-center gap-1"><Database className="h-3 w-3" /> <code className="text-zinc-400">{table}</code></span>
    {sheet && <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> Sheet: "{sheet}"</span>}
  </div>
);

const Limitation: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-1.5 text-xs text-amber-400/80">
    <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500/60" />
    <span>{text}</span>
  </div>
);

const MatchStep: React.FC<{ step: number; label: string; desc: string; color: string }> = ({ step, label, desc, color }) => {
  const c: Record<string, string> = {
    emerald: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/10',
    blue: 'border-blue-500/40 text-blue-400 bg-blue-500/10',
    purple: 'border-purple-500/40 text-purple-400 bg-purple-500/10',
    amber: 'border-amber-500/40 text-amber-400 bg-amber-500/10',
    cyan: 'border-cyan-500/40 text-cyan-400 bg-cyan-500/10',
  };
  return (
    <li className="flex items-start gap-3">
      <span className={`flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold border shrink-0 mt-0.5 ${c[color] || c.emerald}`}>{step}</span>
      <div>
        <div className="text-xs font-medium text-zinc-200">{label}</div>
        <div className="text-[11px] text-zinc-500 leading-relaxed">{desc}</div>
      </div>
    </li>
  );
};

const GapCard: React.FC<{
  number: number;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  problem: string;
  impact: string;
  mitigations: string[];
}> = ({ number, title, severity, problem, impact, mitigations }) => {
  const sevColor: Record<string, string> = { critical: 'red', high: 'amber', medium: 'amber', low: 'zinc' };
  return (
    <Card className="p-4 space-y-2.5">
      <div className="flex items-start gap-3">
        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[hsl(225,15%,15%)] text-xs font-bold text-zinc-300 shrink-0">{number}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{title}</span>
            <Badge color={sevColor[severity]}>{severity}</Badge>
          </div>
        </div>
      </div>
      <div className="ml-9 space-y-2">
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-0.5">Problem</p>
          <p className="text-xs text-zinc-400 leading-relaxed">{problem}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-0.5">Impact</p>
          <p className="text-xs text-red-400/80 leading-relaxed">{impact}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-0.5">Mitigations</p>
          <ul className="space-y-1">
            {mitigations.map((m, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-400">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500/60 mt-0.5 shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};

const SummaryRow: React.FC<{ label: string; desc: string; highlight?: boolean }> = ({ label, desc, highlight }) => (
  <tr className={highlight ? 'bg-emerald-500/5' : ''}>
    <td className={`py-2.5 px-4 font-medium whitespace-nowrap ${highlight ? 'text-emerald-400' : 'text-zinc-200'}`}>{label}</td>
    <td className="py-2.5 px-4 text-zinc-400">{desc}</td>
  </tr>
);

export default RaketechDocs;
